/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useConversations.ts - Hook para gestión de conversaciones
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Hook personalizado para manejar conversaciones de WhatsApp
 * @version 1.0.1-pre.0.17
 * @architecture WASP Frontend → Proxy → WASP Bot
 * @optimization Auto-refresh inteligente que pausa cuando tab inactivo
 * 
 * ENDPOINTS WASP USADOS:
 * - GET /api/whatsapp/wasp/conversations?limit=100 - Listar conversaciones
 * - POST /api/whatsapp/wasp/conversation-delete   - Eliminar conversación
 * 
 * NOTA: Endpoints mapeados desde WASP bot (192.168.112.3:3001)
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { getHatateToken } from '../helpers/utils';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Interface de conversación (compatible con WASP)
 */
export interface Conversation {
  id: string;                    // Ej: "56912345678@c.us"
  name: string;                  // Nombre del contacto o número
  lastMessageTime: string;       // ISO timestamp
  firstMessageTime: string;      // ISO timestamp
  messageCount: number;          // Total de mensajes
  preview: string;               // Preview del último mensaje
  phoneNumber?: string;          // Número extraído del id
  isActive?: boolean;            // Calculado: <5min desde último mensaje
  lastMessageType?: string;      // Tipo del último mensaje
}

/**
 * Opciones del hook
 */
export interface UseConversationsOptions {
  autoRefresh?: boolean;         // Habilitar auto-refresh
  refreshInterval?: number;      // Intervalo en ms (default: 5000)
  onError?: (error: string) => void; // Callback de error
}

/**
 * Return del hook
 */
export interface UseConversationsReturn {
  conversations: Conversation[];
  loading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  refreshConversations: () => Promise<void>;
  deleteConversation: (contactId: string) => Promise<boolean>;
  isConversationActive: (lastActivityTime: string) => boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook personalizado para gestión de conversaciones con WASP
 * 
 * @param options - Opciones de configuración
 * @returns Estado y funciones de conversaciones
 * 
 * @example
 * ```tsx
 * const { conversations, loading, refreshConversations } = useConversations({
 *   autoRefresh: true,
 *   refreshInterval: 5000
 * });
 * ```
 */
export const useConversations = (
  options: UseConversationsOptions = {}
): UseConversationsReturn => {
  const {
    autoRefresh = false,
    refreshInterval = 5000,
    onError
  } = options;

  // Estados
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Refs
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const isTabVisibleRef = useRef<boolean>(true);

  /**
   * Verificar si una conversación está activa (últimos 5 minutos)
   */
  const isConversationActive = useCallback((lastActivityTime: string): boolean => {
    const currentTime = new Date().getTime();
    const lastMessageTime = new Date(lastActivityTime).getTime();
    const fiveMinutesInMs = 5 * 60 * 1000;
    
    return (currentTime - lastMessageTime) <= fiveMinutesInMs;
  }, []);

  /**
   * Obtener lista de conversaciones desde WASP
   */
  const fetchConversations = useCallback(async (): Promise<void> => {
    try {
      const API_TOKEN = getHatateToken();
      
      const response = await axios.get<{
        success: boolean;
        conversationsCount: number;
        conversations: any[];
        timestamp: string;
      }>(`${API_BASE_URL}/conversations`, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN,
          'Content-Type': 'application/json'
        },
        params: {
          limit: 100
        },
        timeout: 30000
      });

      if (!isMountedRef.current) return;

      if (response.data.success && response.data.conversations) {
        // Mapear y enriquecer conversaciones desde WASP
        const conversationsWithActivity = response.data.conversations.map(conv => ({
          id: conv.id,
          name: conv.name || conv.id.split('@')[0],
          lastMessageTime: conv.lastMessageTime,
          firstMessageTime: conv.firstMessageTime,
          messageCount: conv.messageCount || 0,
          preview: conv.preview || '[Sin mensajes]',
          phoneNumber: conv.id.split('@')[0],
          isActive: isConversationActive(conv.lastMessageTime),
          lastMessageType: conv.lastMessageType || 'text'
        }));

        setConversations(conversationsWithActivity);
        setError(null);
        setLastUpdate(new Date());
        
        console.log('✅ Conversaciones cargadas desde WASP:', conversationsWithActivity.length);
        console.log('📋 Conversaciones:', conversationsWithActivity); // ✅ Ver el contenido completo

      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      const errorMessage = err instanceof AxiosError
        ? err.response?.data?.message || err.message
        : 'Error desconocido al cargar conversaciones';

      console.error('❌ Error cargando conversaciones:', errorMessage);
      setError(errorMessage);
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      console.log('🔍 Finally ejecutado, isMounted:', isMountedRef.current);
      if (isMountedRef.current) {
        setLoading(false);
        console.log('🔍 Loading set to false, conversations:', conversations.length);
      }
    }
  }, [isConversationActive, onError]);

  /**
   * Refrescar conversaciones manualmente
   */
  const refreshConversations = useCallback(async (): Promise<void> => {
    setLoading(true);
    await fetchConversations();
  }, [fetchConversations]);

  /**
   * Eliminar una conversación en WASP
   * 
   * @param contactId - ID del contacto/conversación a eliminar
   * @returns true si se eliminó correctamente
   */
  const deleteConversation = useCallback(
    async (contactId: string): Promise<boolean> => {
      try {
        const API_TOKEN = getHatateToken();

        const response = await axios.post<{
          success: boolean;
          message: string;
        }>(
          `${API_BASE_URL}/conversation-delete`,
          { contactId },
          {
            headers: {
              'x-api-key': API_KEY,
              'Authorization': API_TOKEN,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          }
        );

        if (response.data.success) {
          // Actualizar lista local eliminando la conversación
          setConversations(prev => 
            prev.filter(conv => conv.id !== contactId)
          );
          
          console.log('✅ Conversación eliminada:', contactId);
          return true;
        }

        return false;
      } catch (err) {
        const errorMessage = err instanceof AxiosError
          ? err.response?.data?.message || err.message
          : 'Error al eliminar conversación';

        console.error('❌ Error eliminando conversación:', errorMessage);
        setError(errorMessage);
        
        if (onError) {
          onError(errorMessage);
        }
        
        return false;
      }
    },
    [onError]
  );

  /**
   * Fetch inicial al montar el componente
   */
  useEffect(() => {
    isMountedRef.current = true;
    fetchConversations();

   return () => {
      isMountedRef.current = false;
    };
  },[]); // [fetchConversations]);

  /**
   * Configurar auto-refresh si está habilitado
   * OPTIMIZACIÓN: Pausa cuando el tab está inactivo (ahorra recursos)
   */
  /*
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      // Handler para detectar cuando el tab se vuelve visible/invisible
      const handleVisibilityChange = () => {
        isTabVisibleRef.current = !document.hidden;
        
        if (isTabVisibleRef.current) {
          // Tab visible: fetch inmediato y reiniciar intervalo
          console.log('🔄 Tab visible: reactivando auto-refresh');
          fetchConversations();
        } else {
          // Tab invisible: pausar auto-refresh
          console.log('⏸️ Tab invisible: pausando auto-refresh');
        }
      };

      // Iniciar intervalo
      intervalRef.current = setInterval(() => {
        // Solo hacer fetch si el tab está visible
        if (isTabVisibleRef.current) {
          fetchConversations();
        } else {
          console.log('⏸️ Auto-refresh pausado (tab inactivo)');
        }
      }, refreshInterval);

      // Listener para visibilidad
      document.addEventListener('visibilitychange', handleVisibilityChange);

      console.log(`🔄 Auto-refresh activado: cada ${refreshInterval}ms (pausa en tab inactivo)`);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        isMountedRef.current = false;
        console.log('🔄 Auto-refresh desactivado');
      };
    }
  }, [autoRefresh, refreshInterval, fetchConversations]); */
useEffect(() => {
  if (autoRefresh && refreshInterval > 0) {
    const handleVisibilityChange = () => {
      isTabVisibleRef.current = !document.hidden;
      
      if (isTabVisibleRef.current) {
        console.log('🔄 Tab visible: reactivando auto-refresh');
        fetchConversations();
      } else {
        console.log('⏸️ Tab invisible: pausando auto-refresh');
      }
    };

    intervalRef.current = setInterval(() => {
      if (isTabVisibleRef.current) {
        fetchConversations();
      } else {
        console.log('⏸️ Auto-refresh pausado (tab inactivo)');
      }
    }, refreshInterval);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    console.log(`🔄 Auto-refresh activado: cada ${refreshInterval}ms (pausa en tab inactivo)`);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      console.log('🔄 Auto-refresh desactivado');
    };
  }
}, [autoRefresh, refreshInterval]); // ✅ Sin fetchConversations aquí

  /**
   * Return del hook
   */
  return {
    conversations,
    loading,
    error,
    lastUpdate,
    refreshConversations,
    deleteConversation,
    isConversationActive
  };
};

export default useConversations;